import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Loading from "../../Common/Loading";
import CustomAlert from "../../Common/CustomAlert";

// Import Images
import show_n from "../../Common/Images/show_n.gif";
import show_o from "../../Common/Images/show_o.gif";
import modify_n from "../../Common/Images/modify_n.gif";
import modify_o from "../../Common/Images/modify_o.gif";
import delete_n from "../../Common/Images/delete_n.gif";
import delete_o from "../../Common/Images/delete_o.gif";
import add_n from "../../Common/Images/add_n.gif";
import add_o from "../../Common/Images/add_o.gif";
import close_n from "../../Common/Images/close_n.gif";
import close_o from "../../Common/Images/close_o.gif";

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

interface School {
  Country: string;
  SchoolCode: string;
  Name: string;
  Category: string;
  MaxUsers: number;
  MaxSeats: number;
  RegisteredOn: string;
  Status: string;
  Access_StartDate: string;
  Access_EndDate: string;
  Access_FromTime: string;
  Access_ToTime: string;
  ParentSchoolCode: string;
  AllowDirectHomeUse: string;
}

interface DeleteCount {
  TableName: string;
  Description: string;
  Count: number;
}

// Helper for the GIF buttons
const ImageButton = ({
  srcNormal,
  srcOver,
  onClick,
  alt,
}: {
  srcNormal: string;
  srcOver: string;
  onClick?: () => void;
  alt: string;
}) => {
  return (
    <img
      src={srcNormal}
      alt={alt}
      onClick={onClick}
      onMouseOver={(e) => (e.currentTarget.src = srcOver)}
      onMouseOut={(e) => (e.currentTarget.src = srcNormal)}
      onMouseDown={(e) => (e.currentTarget.src = srcNormal)}
      style={{ cursor: "pointer", border: "0", verticalAlign: "middle" }}
    />
  );
};

const EL_ListSchools: React.FC = () => {
  const navigate = useNavigate();
  const [view, setView] = useState<"MAIN" | "CONFIRM">("MAIN");
  const [schools, setSchools] = useState<School[]>([]);
  const [countries, setCountries] = useState<string[]>(["--ALL--"]);
  const [loading, setLoading] = useState<boolean>(false);
  const [ddlType, setDdlType] = useState("ALL");
  const [cboCountry, setCboCountry] = useState("--ALL--");
  const [selectedCode, setSelectedCode] = useState<string>("");
  const [hasSearched, setHasSearched] = useState(false);
  const [deleteCounts, setDeleteCounts] = useState<DeleteCount[]>([]);
  const [alertInfo, setAlertInfo] = useState({ show: false, message: "" });

  useEffect(() => {
    const prevBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = "white";

    loadCountries();

    return () => {
      document.body.style.backgroundColor = prevBg;
    };
  }, []);
  const loadCountries = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/get_countries/`);

      if (res.ok) {
        const data = await res.json();

        console.log("countries API", data);

        const list = data.countries;

        setCountries(["--ALL--", ...list]);
      }
    } catch (error) {
      console.error("Error loading countries", error);
    }
  };
  const fetchSchools = async () => {
    setLoading(true);
    setSelectedCode("");
    setHasSearched(true);

    const params = new URLSearchParams();
    params.append("country", cboCountry);
    params.append("category", ddlType);

    try {
      const res = await fetch(
        `${API_BASE_URL}api/manage-schools/?${params.toString()}`,
      );
      if (res.ok) {
        const data = await res.json();
        setSchools(data);
      }
    } catch (error) {
      setAlertInfo({ show: true, message: "Error fetching data" });
    } finally {
      setLoading(false);
    }
  };

  const handleModify = () => {
    if (!hasSearched || schools.length === 0) return;
    if (!selectedCode) {
      setAlertInfo({ show: true, message: "Please Select a row." });
      return;
    }
    navigate(
      `/WebAdmin/SCHOOLS/EL_SchoolDetails?SchoolCode=${selectedCode}&CalledFrom=L`,
    );
  };

  const handleAdd = () => {
    navigate(`/WebAdmin/SCHOOLS/EL_AddNewSchoolCode?CalledFrom=L`);
  };

  const handleClose = () => {
    navigate("/SysAdmin");
  };

  const handleDeleteCheck = async () => {
    if (!hasSearched || schools.length === 0) return;
    if (!selectedCode) {
      setAlertInfo({ show: true, message: "Please Select a row." });
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({ school_code: selectedCode });
      const res = await fetch(
        `${API_BASE_URL}api/manage-schools/check_delete/?${params.toString()}`,
      );
      if (res.ok) {
        const data = await res.json();
        setDeleteCounts(data);
        setView("CONFIRM");
      }
    } catch (error) {
      setAlertInfo({ show: true, message: "Error checking dependencies" });
    } finally {
      setLoading(false);
    }
  };

  const performDelete = async () => {
    if (!selectedCode) return;

    setLoading(true);

    try {
      const res = await fetch(`${API_BASE_URL}api/manage-schools/delete/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ school_code: selectedCode }),
      });

      if (res.ok) {
        setView("MAIN");
        await fetchSchools();
      } else {
        let message = "Error deleting school.";

        try {
          const data = await res.json();
          message = data?.error || data?.message || message;
        } catch {
          // Keep default message
        }

        setAlertInfo({
          show: true,
          message,
        });
      }
    } catch (error) {
      console.error("Error deleting school:", error);

      setAlertInfo({
        show: true,
        message: "Network error while deleting school.",
      });
    } finally {
      setLoading(false);
    }
  };

  const HEADER_HEIGHT = 32;

  const styles = {
    container: {
      fontFamily: "Arial",
      fontSize: "11px",
      width: "100%",
      height: "100vh",
      display: "flex",
      flexDirection: "column" as const,
    },
    topBar: {
      padding: "5px",
      marginBottom: "5px",
      display: "flex",
      alignItems: "center",
      fontSize: "12pt",
    },
    select: {
      fontSize: "12pt",
      marginRight: "20px",
      marginLeft: "5px",
      minWidth: "150px",
    },
    gridContainer: {
      flex: 1,
      overflowX: "auto" as const,
      overflowY: "auto" as const,
      border: "1px solid #80A0FF",
      backgroundColor: "white",
    },
    table: {
      width: "max-content",
      minWidth: "100%",
      borderCollapse: "collapse" as const,
      fontSize: "9pt",
      tableLayout: "auto" as const,
    },
    th: {
      backgroundColor: "#C7DDFD",
      color: "#2859ac",
      borderRight: "1px solid #80A0FF",
      borderBottom: "1px solid #80A0FF",
      padding: "8px",
      textAlign: "left" as const,
      fontWeight: "normal" as const,
      whiteSpace: "nowrap" as const,
      position: "sticky" as const,
      zIndex: 10,
      height: `${HEADER_HEIGHT}px`,
      boxSizing: "border-box" as const,
      fontSize: "10pt",
      verticalAlign: "bottom"
    },
    thSub: {
      backgroundColor: "#C7DDFD",
      color: "#2859ac",
      borderRight: "1px solid #80A0FF",
      borderBottom: "1px solid #80A0FF",
      padding: "3px",
      textAlign: "left" as const,
      fontWeight: "normal" as const,
      whiteSpace: "nowrap" as const,
      position: "sticky" as const,
      top: `${HEADER_HEIGHT}px`,
      zIndex: 10,
      height: `${HEADER_HEIGHT}px`,
      boxSizing: "border-box" as const,
    },
    td: {
      borderRight: "1px solid #80A0FF",
      // borderBottom: "1px solid #80A0FF",
      padding: "8px",
      fontSize: "10pt",
      whiteSpace: "nowrap" as const,
    },
    bottomBar: {
      textAlign: "center" as const,
      padding: "10px 0px 60px 10px",
      borderTop: "1px solid #80A0FF",
      backgroundColor: "white",
    },
  };

  if (view === "CONFIRM") {
    return (
      <div
        style={{
          ...styles.container,
          padding: "20px",
          alignItems: "center",
          boxSizing: "border-box",
          width: "100%",
          height: "100vh",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: "100%",
            flex: 1,
            border: "1px solid #80A0FF",
            overflowY: "auto",
            overflowX: "auto",
            marginBottom: "20px",
            background: "white",
          }}
        >
          <div
            style={{
              backgroundColor: "maroon",
              color: "white",
              fontWeight: "bold",
              textAlign: "center",
              padding: "8px",
              position: "sticky",
              top: 0,
              zIndex: 2,
              fontSize: "10pt",
            }}
          >
            List of records available for School Code: {selectedCode}
          </div>

          <div
            style={{
              width: "100%",
              overflowX: "auto",
              overflowY: "hidden",
            }}
          >
            <table
              style={{
                ...styles.table,
                width: "100%",
                minWidth: "900px",
                tableLayout: "auto",
                borderCollapse: "collapse",
                fontSize: "10pt",
              }}
            >
              <colgroup>
                <col style={{ width: "1%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "32%" }} />
                <col style={{ width: "4%" }} />
                <col style={{ width: "49%" }} />
              </colgroup>
              <thead>
                <tr style={{ backgroundColor: "maroon", color: "white", }}>
                  <th
                    style={{
                      padding: "8px",
                      color: "white",
                      textAlign: "left",
                      position: "sticky",
                      top: 0,
                      border: "1px solid #80A0FF",
                      fontSize: "10pt", textWrap: "nowrap", whiteSpace: "nowrap", fontWeight: "normal"
                    }}
                  >
                    #
                  </th>
                  <th
                    style={{
                      padding: "4px",
                      color: "white",
                      textAlign: "left",
                      position: "sticky",
                      top: 0,
                      border: "1px solid #80A0FF", fontWeight: "normal"
                    }}
                  >
                    TableName
                  </th>
                  <th
                    style={{
                      padding: "4px",
                      color: "white",
                      textAlign: "left",
                      position: "sticky",
                      top: 0,
                      border: "1px solid #80A0FF", fontWeight: "normal"
                    }}
                  >
                    TableDesc
                  </th>
                  <th
                    style={{
                      padding: "4px",
                      color: "white",
                      textAlign: "center",
                      position: "sticky",
                      top: 0,
                      border: "1px solid #80A0FF", fontWeight: "normal"
                    }}
                  >
                    RecCount
                  </th>
                  <th
                    style={{
                      padding: "4px",
                      color: "white",
                      textAlign: "left",
                      position: "sticky",
                      top: 0,
                      border: "1px solid #80A0FF", fontWeight: "normal"
                    }}
                  >
                    Remarks
                  </th>
                </tr>
              </thead>
              <tbody>
                {deleteCounts.map((row, i) => {
                  const zebraColor = i % 2 === 0 ? "white" : "#F4FAFF";

                  return (
                    <tr key={i} style={{ backgroundColor: zebraColor }}>
                      <td
                        style={{ border: "1px solid #80A0FF", textAlign: "center", padding: "8px" }}
                      >
                        {i + 1}
                      </td>

                      <td
                        style={{ border: "1px solid #80A0FF", padding: "2px" }}
                      >
                        {row.TableName}
                      </td>

                      <td
                        style={{
                          border: "1px solid #80A0FF",
                          padding: "2px",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        }}
                      >
                        {row.Description}
                      </td>

                      <td
                        style={{
                          border: "1px solid #80A0FF",
                          padding: "2px",
                          textAlign: "right",
                        }}
                      >
                        {row.Count}
                      </td>

                      <td
                        style={{ border: "1px solid #80A0FF", padding: "2px" }}
                      />
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <h3
          style={{
            color: "black",
            textDecoration: "underline",
            fontSize: "12pt",
            textAlign: "center",
            margin: "0 0 15px",
          }}
        >
          Confirm to Delete following records permanently from Database
        </h3>

        <div
          style={{
            marginBottom: "20px",
            fontSize: "12pt",
            textAlign: "center",
          }}
        >
          Do you Really Want to DELETE these Records from Database Permanently?
        </div>
        <div
          style={{
            display: "flex",
            gap: "12px",
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >

          {loading && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "15px",
              }}
            >
              <Loading />
            </div>
          )}
          <button
            onClick={performDelete}
            disabled={loading}
            style={{
              backgroundColor: "red",
              color: "white",
              fontWeight: "bold",
              marginRight: "10px",
              padding: "5px 15px",
            }}
          >
            OK to Delete
          </button>
          <button
            onClick={() => setView("MAIN")}
            disabled={loading}
            style={{
              backgroundColor: "green",
              color: "white",
              fontWeight: "bold",
              padding: "5px 15px",
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Top Filter Bar */}
      <div style={styles.topBar}>
        <span>Select School Type:</span>
        <select
          value={ddlType}
          onChange={(e) => setDdlType(e.target.value)}
          style={styles.select}
        >
          <option value="ALL">ALL Types</option>
          <option value="DLR">Dealer</option>
          <option value="ISP">ISP</option>
          <option value="LIB">Library</option>
          <option value="SCH">School</option>
        </select>

        <span style={{ marginLeft: "20px" }}>Country</span>
        <select
          value={cboCountry}
          onChange={(e) => setCboCountry(e.target.value)}
          style={styles.select}
        >
          {countries.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>

        <div style={{ flex: 1, textAlign: "right" }}>
          <ImageButton
            srcNormal={show_n}
            srcOver={show_o}
            onClick={fetchSchools}
            alt="Show"
          />
        </div>
      </div>

      {hasSearched && (
        <div style={styles.gridContainer}>
          <table style={styles.table}>
            <thead>
              {/* Header Row 1 */}
              <tr>
                <th
                  rowSpan={2}
                  style={{ ...styles.th, top: 0, width: "45px", textAlign: "center" }}
                >
                  #
                </th>

                <th
                  rowSpan={2}
                  style={{ ...styles.th, top: 0, width: "90px" }}
                >
                  Country
                </th>

                <th
                  rowSpan={2}
                  style={{ ...styles.th, top: 0, width: "120px" }}
                >
                  School Code
                </th>

                <th
                  rowSpan={2}
                  style={{ ...styles.th, top: 0, width: "250px" }}
                >
                  School Name
                </th>

                <th
                  rowSpan={2}
                  style={{ ...styles.th, top: 0, width: "80px" }}
                >
                  Category
                </th>

                <th
                  colSpan={2}
                  style={{
                    ...styles.th,
                    top: 0,
                    width: "120px",
                    textAlign: "center",
                  }}
                >
                  Max
                </th>

                <th
                  rowSpan={2}
                  style={{ ...styles.th, top: 0, width: "100px" }}
                >
                  Regd.on
                </th>

                <th
                  rowSpan={2}
                  style={{ ...styles.th, top: 0, width: "70px" }}
                >
                  Status
                </th>

                <th
                  colSpan={2}
                  style={{
                    ...styles.th,
                    top: 0,
                    width: "180px",
                    textAlign: "center",
                  }}
                >
                  Access Date
                </th>

                <th
                  colSpan={2}
                  style={{
                    ...styles.th,
                    top: 0,
                    width: "140px",
                    textAlign: "center",
                  }}
                >
                  Between
                </th>

                <th
                  rowSpan={2}
                  style={{ ...styles.th, top: 0, width: "170px" }}
                >
                  Parent School Code
                </th>

                <th
                  rowSpan={2}
                  style={{
                    ...styles.th,
                    top: 0,
                    minWidth: "120px",
                    width: "100%",
                  }}
                >
                  Home Use
                </th>
              </tr>
              {/* Header Row 2 - Uses thSub with calculated top offset */}
              <tr>
                <th style={{ ...styles.thSub, width: "60px" }}>Users</th>
                <th style={{ ...styles.thSub, width: "60px" }}>Seats</th>

                <th style={{ ...styles.thSub, width: "90px" }}>Start</th>
                <th style={{ ...styles.thSub, width: "90px" }}>End</th>

                <th style={{ ...styles.thSub, width: "70px" }}>Time</th>
                <th style={{ ...styles.thSub, width: "70px" }}>Time</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td
                    colSpan={15}
                    style={{ padding: "20px", textAlign: "center" }}
                  >
                    <Loading />
                  </td>
                </tr>
              )}
              {!loading && hasSearched && schools.length === 0 && (
                <tr>
                  <td
                    colSpan={15}
                    style={{ padding: "20px", textAlign: "center" }}
                  >
                    No records found
                  </td>
                </tr>
              )}
              {!loading &&
                schools.map((row, index) => {
                  const isSelected = selectedCode === row.SchoolCode;
                  const zebraColor = index % 2 === 0 ? "white" : "#F4FAFF";
                  const bg = isSelected ? "orange" : zebraColor;
                  const textColor = isSelected ? "white" : "black";

                  return (
                    <tr
                      key={row.SchoolCode}
                      onClick={() => setSelectedCode(row.SchoolCode)}
                      style={{
                        backgroundColor: bg,
                        color: textColor,
                        cursor: "pointer",
                      }}
                    >
                      <td style={styles.td}>{index + 1}</td>
                      <td style={styles.td}>{row.Country}</td>
                      <td style={styles.td}>{row.SchoolCode}</td>
                      <td style={styles.td}>{row.Name}</td>
                      <td style={styles.td}>{row.Category}</td>
                      <td style={{ ...styles.td, textAlign: "right" }}>
                        {row.MaxUsers}
                      </td>
                      <td style={{ ...styles.td, textAlign: "right" }}>
                        {row.MaxSeats}
                      </td>
                      <td style={styles.td}>{row.RegisteredOn}</td>
                      <td style={styles.td}>{row.Status}</td>
                      <td style={styles.td}>{row.Access_StartDate}</td>
                      <td style={styles.td}>{row.Access_EndDate}</td>
                      <td style={styles.td}>{row.Access_FromTime}</td>
                      <td style={styles.td}>{row.Access_ToTime}</td>
                      <td style={styles.td}>{row.ParentSchoolCode}</td>
                      <td style={styles.td}>{row.AllowDirectHomeUse}</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}
      {alertInfo.show && (
        <CustomAlert
          message={alertInfo.message}
          onClose={() => setAlertInfo({ ...alertInfo, show: false })}
        />
      )}

      <div style={styles.bottomBar}>
        <ImageButton
          srcNormal={modify_n}
          srcOver={modify_o}
          onClick={handleModify}
          alt="Modify"
        />
        <span style={{ margin: "0 10px" }}></span>
        <ImageButton
          srcNormal={delete_n}
          srcOver={delete_o}
          onClick={handleDeleteCheck}
          alt="Delete"
        />
        <span style={{ margin: "0 10px" }}></span>
        <ImageButton
          srcNormal={add_n}
          srcOver={add_o}
          onClick={handleAdd}
          alt="Add"
        />
        <span style={{ margin: "0 10px" }}></span>
        <ImageButton
          srcNormal={close_n}
          srcOver={close_o}
          onClick={handleClose}
          alt="Close"
        />
      </div>
    </div>
  );
};

export default EL_ListSchools;
